import type { BoardPatch, BoardSnapshot, BoardStroke, Point } from '../types';

export type BoardEngineOptions = {
  color: string;
  lineWidth: number;
  onPatch?: (patch: BoardPatch) => void;
};

function opId() {
  return `board-op-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function strokeId() {
  return `board-stroke-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function hexToArgb(color: string) {
  const normalized = color.replace('#', '');
  const rgb = Number.parseInt(
    normalized.length === 3
      ? normalized.split('').map((item) => item + item).join('')
      : normalized,
    16,
  );
  if (Number.isNaN(rgb)) {
    return 0xFF1B1F24;
  }
  return 0xFF000000 + rgb;
}

function argbToCss(color: number) {
  return `#${(color & 0xFFFFFF).toString(16).padStart(6, '0')}`;
}

export class BoardEngine {
  private ctx: CanvasRenderingContext2D;
  private drawingStroke: BoardStroke | null = null;
  private lastPoint: Point | null = null;
  private color: string;
  private lineWidth: number;
  private onPatch: (patch: BoardPatch) => void;
  private removeListeners: Array<() => void> = [];

  constructor(private readonly canvas: HTMLCanvasElement, options: BoardEngineOptions) {
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context unavailable');
    }
    this.ctx = context;
    this.color = options.color;
    this.lineWidth = options.lineWidth;
    this.onPatch = options.onPatch ?? (() => {});
    this.resetCanvas();
    this.bind();
  }

  setStyle(style: { color?: string; lineWidth?: number }) {
    this.color = style.color ?? this.color;
    this.lineWidth = style.lineWidth ?? this.lineWidth;
  }

  clear(emit = true) {
    this.resetCanvas();
    if (emit) {
      this.onPatch({ type: 'clear', opId: opId() });
    }
  }

  applyPatch(patch: BoardPatch) {
    if ('type' in patch && patch.type === 'clear') {
      this.resetCanvas();
      return;
    }
    if ('points' in patch) {
      this.drawStroke(patch);
    }
  }

  replaceSnapshot(snapshot: BoardSnapshot) {
    this.resetCanvas();
    snapshot.patches.forEach((patch) => this.applyPatch(patch));
  }

  destroy() {
    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];
  }

  private resetCanvas() {
    this.ctx.fillStyle = '#fff';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private bind() {
    const onPointerDown = (event: PointerEvent) => {
      const point = this.eventPoint(event);
      this.drawingStroke = {
        id: strokeId(),
        color: hexToArgb(this.color),
        width: this.lineWidth,
        points: [point],
      };
      this.lastPoint = point;
      this.canvas.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!this.drawingStroke || !this.lastPoint) {
        return;
      }
      const point = this.eventPoint(event);
      this.drawSegment(this.lastPoint, point, this.drawingStroke.color, this.drawingStroke.width);
      this.drawingStroke.points.push(point);
      this.lastPoint = point;
    };

    const onPointerUp = () => {
      if (!this.drawingStroke) {
        return;
      }
      if (this.drawingStroke.points.length >= 2) {
        this.onPatch(this.drawingStroke);
      }
      this.drawingStroke = null;
      this.lastPoint = null;
    };

    this.canvas.addEventListener('pointerdown', onPointerDown);
    this.canvas.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    this.removeListeners.push(
      () => this.canvas.removeEventListener('pointerdown', onPointerDown),
      () => this.canvas.removeEventListener('pointermove', onPointerMove),
      () => window.removeEventListener('pointerup', onPointerUp),
    );
  }

  private eventPoint(event: PointerEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width),
      y: ((event.clientY - rect.top) / rect.height),
    };
  }

  private denormalize(point: Point): Point {
    return {
      x: point.x * this.canvas.width,
      y: point.y * this.canvas.height,
    };
  }

  private drawStroke(stroke: BoardStroke) {
    if (stroke.points.length < 2) {
      return;
    }
    for (let index = 1; index < stroke.points.length; index += 1) {
      this.drawSegment(stroke.points[index - 1], stroke.points[index], stroke.color, stroke.width);
    }
  }

  private drawSegment(from: Point, to: Point, color: number, width: number) {
    const start = this.denormalize(from);
    const end = this.denormalize(to);
    this.ctx.beginPath();
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = argbToCss(color);
    this.ctx.lineWidth = width;
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.stroke();
    this.ctx.closePath();
  }
}
